import { getState } from '../store';

/** Get elapsed seconds since locationStartedAt, accounting for demo speed multiplier */
export function getElapsedSec(startedAt: number | null): number {
  if (!startedAt) return 0;
  const multiplier = getState().demoSpeedMultiplier || 1;
  return ((Date.now() - startedAt) / 1000) * multiplier;
}

/**
 * Session 50 post-ship fix #2 — compute a clock-skew-corrected local
 * anchor timestamp from (serverNow, serverStampedOrigin). The PWA timer
 * uses `Date.now() - state.locationStartedAt` to show elapsed time; if we
 * assign `locationStartedAt = serverStampedOrigin` directly, the math mixes
 * two clocks (server's and device's) and a 3s-fast phone sees 3s MORE
 * elapsed than teammates. Instead we translate the server origin into the
 * device's local clock frame:
 *
 *     localEquivalent = Date.now() - (serverNow - serverStampedOrigin)
 *
 * After translation, `Date.now() - localEquivalent` always equals the
 * server's true elapsed at the moment of translation, plus whatever tick
 * time has passed since. Self-consistent on each device, regardless of
 * how much the device clock drifts from server time.
 *
 * Returns `null` if either input is null/undefined — the caller should
 * fall back to `Date.now()` in that case (pre-migration legacy rows,
 * spectators, etc).
 */
export function computeLocalAnchor(
  serverStampedOrigin: number | null | undefined,
  serverNow: number | null | undefined
): number | null {
  if (serverStampedOrigin == null) return null;
  // When the server didn't include serverNow, we can't correct for skew.
  // Assume zero skew — caller gets the raw server value. Worse than a
  // correction but better than falling all the way back to Date.now()
  // which ignores the team's shared anchor entirely.
  if (serverNow == null) return serverStampedOrigin;
  const serverElapsed = serverNow - serverStampedOrigin;
  return Date.now() - serverElapsed;
}

/**
 * /autoplan 2026-04-24 — every server-anchored `locationStartedAt` write
 * site collapses `computeLocalAnchor(origin, now) ?? Date.now()` into this
 * one helper. Centralises the fall-back policy so a future invariant change
 * (e.g. "log a warning if both args are null") is a one-file edit.
 *
 * Call sites that MUST use this helper (pinned by source-contract test):
 *   - join-view.ts (first/resume join, gameStarted=true branch)
 *   - location-view.ts (goTo for WS ADVANCE/CAUGHT_UP, site-code submit, auto-advance)
 *   - auto-advance.ts (online timeout/skip — NOT offline path)
 *
 * Legitimate direct `Date.now()` writes to `locationStartedAt`:
 *   - `auto-advance.ts:handleOfflineTimeout` — offline path, no server roundtrip.
 *   - `lobby-view.ts` GAME_STARTED handler — backend `startGame` re-stamps
 *     `Teams.locationEnteredAt = now()` inside its transaction, so client's
 *     Date.now() ≈ server's re-stamp moment. No per-stage anchor needed.
 */
export function anchorFromServer(
  serverStampedOrigin: number | null | undefined,
  serverNow: number | null | undefined
): number {
  return computeLocalAnchor(serverStampedOrigin, serverNow) ?? Date.now();
}
